import {
    AssetCategoryParameterChild,
    AssetCategoryParameterType,
    AssetCategoryParameterUpdateRequest,
    AssetCategoryUpdateRequest,
    AssetClassificationTypeUpdateRequest,
    IAssetCategoryParameterChild,
    IAssetCategoryUpdateRequest,
    IAssetClassificationTypeUpdateRequest,
} from '@kcftech/prometheustypescriptapiclient';
import { sdClient } from 'contexts/SDKClientContext';
import { removeUndefinedProperties } from 'utils/object';
import { Lookup } from 'utils/types';

/**
 * Updates a asset type given its id and the updates to make. Loads the
 * asset type to get all current information and adds the updates. Helps prevent getting
 * out of sync with the api or not passing in fields and accidentally clearing them.
 * @param assetTypeId The id of the asset type to update
 * @param updates The fields to update on the asset type
 */
export async function updateAssetType(
    assetTypeId: number,
    updates: Partial<IAssetClassificationTypeUpdateRequest>
) {
    const { Name, Active } = await sdClient.getAssetClassificationType({
        typeId: assetTypeId,
    });

    const request = new AssetClassificationTypeUpdateRequest({
        Name,
        Active,
        ...removeUndefinedProperties(updates),
    });

    return sdClient.updateAssetClassificationType({ typeId: assetTypeId, body: request });
}

/**
 * Lookup to get formatted name for `AssetCategoryParameterType`.
 */
export const formattedParameterTypes: Lookup<AssetCategoryParameterType, string> = {
    [AssetCategoryParameterType.Bool]: 'Toggle',
    [AssetCategoryParameterType.Integer]: 'Integer',
    [AssetCategoryParameterType.Decimal]: 'Decimal',
    [AssetCategoryParameterType.Text]: 'Text',
    [AssetCategoryParameterType.Selection]: 'Selection',
    [AssetCategoryParameterType.MonitoringPointId]: 'Monitoring Point',
};

/**
 * Updates a asset category given its id and the updates to make. Loads the
 * asset category to get all current information and adds the updates. Helps prevent getting
 * out of sync with the api or not passing in fields and accidentally clearing them.
 * @param categoryId The id of the asset category to update
 * @param updates The fields to update on the asset category
 */
export async function updateAssetCategory(
    categoryId: number,
    updates: Partial<IAssetCategoryUpdateRequest>
) {
    const { ShortName, ParentCategory } = await sdClient.getAssetClassificationCategory({
        categoryId,
    });

    const request = new AssetCategoryUpdateRequest({
        ShortName: ShortName ?? '',
        ParentCategoryId: ParentCategory?.AssetCategoryParentId,
        ...updates,
    });

    return sdClient.updateAssetClassificationCategory({ categoryId, body: request });
}

/**
 * Loads asset category parameters.
 *
 * @param categoryId The parent category.
 * @param includeInherited If inherited parameters should be included.
 */
export async function getAssetCategoryParameters(
    categoryId: number,
    includeInherited = false,
    removeDuplicates = true
) {
    const parameters = await sdClient.getAssetCategoryParameters({
        categoryId,
        removeDuplicates,
    });
    return includeInherited
        ? parameters
        : parameters.filter((param) => param.Category.AssetCategoryId === categoryId);
}

/**
 * Creates a asset category parameter given its category and the new parameter.
 * Loads all the asset parameters to get all current information and adds the new parameter.
 * @param categoryId The id of the category the parameter belongs to
 * @param parameter The new asset category parameter
 */
export async function createAssetParameter(
    categoryId: number,
    parameter: IAssetCategoryParameterChild
) {
    const parameters = await getAssetCategoryParameters(categoryId);

    const request = new AssetCategoryParameterUpdateRequest({
        Parameters: getChildParameters([...parameters, parameter]),
    });

    return sdClient.updateAssetClassificationParameters({ categoryId, body: request });
}

/**
 * Deletes a asset category parameter given its category and the parameter code.
 * Loads all the asset parameters to get all current information and
 * removes the parameter with the code.
 * @param categoryId The id of the category the parameter belongs to
 * @param parameterCode The asset category parameter code to be removed
 */
export async function deleteAssetParameter(categoryId: number, parameterCode: string) {
    const parameters = await getAssetCategoryParameters(categoryId);

    const request = new AssetCategoryParameterUpdateRequest({
        Parameters: getChildParameters(
            parameters.filter((parameter) => parameter.Code !== parameterCode)
        ),
    });

    return sdClient.updateAssetClassificationParameters({ categoryId, body: request });
}

/**
 * Updates a asset category parameter given its category, code and the parameter.
 * Loads all the asset parameters to get all current information, removes the parameter
 * with the code and then adds the parameter changes.
 * @param categoryId The id of the category the parameter belongs to
 * @param parameterCode The asset category parameter code used to edit
 * @param parameter The new asset category parameter
 */
export async function updateAssetParameter(
    categoryId: number,
    parameterCode: string,
    parameter: IAssetCategoryParameterChild
) {
    const parameters = await getAssetCategoryParameters(categoryId);

    const request = new AssetCategoryParameterUpdateRequest({
        Parameters: getChildParameters([
            ...parameters.filter((param) => param.Code !== parameterCode),
            parameter,
        ]),
    });

    return sdClient.updateAssetClassificationParameters({ categoryId, body: request });
}

const getChildParameters = (parameters: IAssetCategoryParameterChild[]) =>
    parameters.map(
        (parameter) =>
            new AssetCategoryParameterChild({
                ValidValues: parameter.ValidValues,
                Code: parameter.Code,
                Name: parameter.Name,
                CategoryParameterType: parameter.CategoryParameterType,
                MinValue: parameter.MinValue,
                MinValueInclusive:
                    parameter.MinValue !== undefined
                        ? parameter.MinValueInclusive
                        : undefined,
                MaxValue: parameter.MaxValue,
                MaxValueInclusive:
                    parameter.MaxValue !== undefined
                        ? parameter.MaxValueInclusive
                        : undefined,
                DefaultValue: parameter.DefaultValue,
            })
    );
